import {
  MoveHorizontal, Minimize2, Maximize2, Scissors,
  Shrink, Zap, Wind,
  Ruler, Frown, CircleHelp,
  type LucideIcon,
} from 'lucide-react'
import type { PainPoint } from '@/types/quiz'

export type PainPointCategory = 'bottom' | 'mid' | 'universal'

export interface PainPointOption {
  id: PainPoint
  label: string
  category: PainPointCategory
  /** Lucide icon component rendered inside the card's icon slot. */
  Icon: LucideIcon
}

/**
 * The 10 pain-point cards on Screen 2. Ordering matches the brand doc groupings
 * so the card grid reads naturally: bottom-heavy → mid-section → universal.
 *
 * Icons are loose visual cues, not literal representations - chosen so each
 * card is visually distinct at a glance. Maria can swap any individual icon
 * by changing the Icon import here without touching the card component.
 *
 * Copy is verbatim from the brand spec - do not rephrase without sign-off.
 */
export const PAIN_POINTS: readonly PainPointOption[] = [
  // Bottom-heavy (Zali-weighted)
  { id: 'waist_gap',     label: 'Waistband gapes at the back',                category: 'bottom',    Icon: MoveHorizontal },
  { id: 'tight_thighs',  label: 'Thighs and bum feel too tight',              category: 'bottom',    Icon: Minimize2      },
  { id: 'sized_up_hips', label: "I've had to size up just to fit my hips",    category: 'bottom',    Icon: Maximize2      },
  { id: 'crotch_rips',   label: 'The crotch rips or splits',                  category: 'bottom',    Icon: Scissors       },

  // Mid-section (Mila / Beatrice-weighted)
  { id: 'waist_digs',    label: 'The waistband digs in or feels too tight',   category: 'mid',       Icon: Shrink         },
  { id: 'buttons_pop',   label: 'Buttons pop open when I bend or sit',        category: 'mid',       Icon: Zap            },
  { id: 'baggy_legs',    label: 'Too much fabric around my legs',             category: 'mid',       Icon: Wind           },

  // Universal (validation only)
  { id: 'length_wrong',  label: 'I can never find the right length',          category: 'universal', Icon: Ruler          },
  { id: 'wears_mens',    label: "I've given up and just wear men's pants",    category: 'universal', Icon: Frown          },
  { id: 'none',          label: 'None of the above - I just want the best fit', category: 'universal', Icon: CircleHelp   },
] as const
